"use client";
/**
 * Renders supporting content whose wrapper stretches across the screen (for use in section content).
 */

import React, { useRef } from "react";
import styled, { css, keyframes } from "styled-components";

import useRect from "../../hooks/useRect";
import { AssetType } from "../../constants/types";

import Box, { BoxProps } from "../Box";
import ImageView from "../ImageView";
import LottieAnimation from "../LottieAnimation";
import ActivityIndicator from "../ActivityIndicator";
import Video from "../Video";
import { Asset } from "../../models";
import { getColor } from "../utils";

export const stretchingVideoTestId = "stretching-asset-video";
export const stretchingImageTestId = "stretching-asset-image";
export const stretchingLottieTestId = "stretching-asset-lottie";

type Props = {
  asset: Asset;
  imageSize?: "small" | "medium" | "large" | "full";
  maxHeight?: number;
  className?: string;
  pointerEvents?: "none" | "auto";
  style?: React.CSSProperties;
} & BoxProps;

const spinning = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(960deg);
  }
`;

type ContainerProps = { isImage: boolean; pointerEvents?: "none" | "auto" } & BoxProps;
const StretchingAssetContainer = styled(Box).attrs<ContainerProps>(props => {
  return {
    width: "100%",
    borderRadius: props.borderRadius || "default",
    p: props.p || "m",
    pointerEvents: props.pointerEvents || "auto",
  };
})<ContainerProps>`
  ${props =>
    props.isImage &&
    css`
      & svg {
        animation: ${spinning} 1s ease-in-out infinite alternate;
        transform-origin: 50% 50%;
      }

      & svg::selection {
        background: transparent;
      }
    `}
`;

const MotionContainer = styled(Box)`
  position: relative;
`;

const LoadingContainer = styled(Box)`
  width: 100%;
`;

const StretchingAsset: React.FC<Props> = ({
  asset,
  className,
  background,
  maxHeight,
  imageSize,
  pointerEvents,
  ...rest
}) => {
  const measuredRef = useRef<HTMLDivElement>(null);
  const { width } = useRect(measuredRef);

  const paddingInt = getContainerPadding(measuredRef.current);
  const height = getStretchingAssetHeight(asset.dimensions, width - paddingInt, maxHeight);

  const isImage = [AssetType.jpg, AssetType.png, AssetType.gif].includes(asset.type),
    isGif = [AssetType.gif].includes(asset.type),
    isLottie = [AssetType.lottie].includes(asset.type),
    isVideo = [AssetType.mp4, AssetType.mov].includes(asset.type);

  const thumbnailMap = {
    large: asset.thumbnails ? asset.thumbnails["1232"] : asset.permalink,
    medium: asset.thumbnails ? asset.thumbnails["480"] : asset.permalink,
    small: asset.thumbnails ? asset.thumbnails["292"] : asset.permalink,
    full: asset.permalink,
  };

  const isProcessing = asset.meta && asset.meta.assetProcessing === "processing";

  function responsiveImageUrl() {
    // If it's a GIF, return the original file, as the generated thumbs are static
    if (isGif) return thumbnailMap.full;

    // If an image size has been specified, return that one
    if (imageSize && thumbnailMap[imageSize]) {
      return thumbnailMap[imageSize];
    }

    // If no explicit image size is set, load the proper thumnail size given the ref width
    const [assetWidth, assetHeight] = asset.dimensions.split("x").map(d => parseInt(d, 10));
    const containerSize = Math.max(width, height),
      assetSize = Math.max(assetWidth, assetHeight),
      desiredSize = Math.min(assetSize, containerSize);

    if (desiredSize === 0) return null;
    if (desiredSize <= 292) return thumbnailMap.small;
    if (desiredSize <= 480) return thumbnailMap.medium;
    if (desiredSize <= 1232) return thumbnailMap.large;
    return thumbnailMap.full;
  }

  function renderAssetComponent() {
    // Loading container has an arbitrary fallback height of 120px for when user
    // uploads content and it's still processing without dimension data.

    if (isProcessing)
      return (
        <LoadingContainer height={height || "120px"}>
          <ActivityIndicator size="small" center color={getColor("grayDark")} />
        </LoadingContainer>
      );

    if (isVideo)
      return (
        <MotionContainer data-testid={stretchingVideoTestId} {...{ height }}>
          <Video
            src={asset.permalink}
            placeholder={responsiveImageUrl()}
            dimensions={asset.dimensions}
            type={asset.type}
            muted={false}
          />
        </MotionContainer>
      );

    if (isLottie)
      return (
        <MotionContainer data-testid={stretchingLottieTestId} {...{ height }}>
          <LottieAnimation permalink={asset.permalink} />
        </MotionContainer>
      );

    return (
      <ImageView
        id={stretchingImageTestId}
        src={responsiveImageUrl()}
        icon="spinner"
        background={background}
        height={height}
      />
    );
  }

  return (
    <Box ref={measuredRef}>
      <StretchingAssetContainer
        className={className}
        background={background}
        isImage={isImage}
        pointerEvents={pointerEvents}
        {...rest}>
        {renderAssetComponent()}
      </StretchingAssetContainer>
    </Box>
  );
};

const getContainerPadding = (el: HTMLElement | null) => {
  if (!el) return 0;

  const extractPixelInt = (computedStyle: CSSStyleDeclaration, propertyName: string) => {
    const propVal = computedStyle.getPropertyValue(propertyName);
    const propValNum = propVal.match(/[0-9]+/);
    if (propValNum !== null) return parseInt(propValNum[0], 10);
    return 0;
  };

  const compStyle = getComputedStyle(el);
  return extractPixelInt(compStyle, "padding-left") + extractPixelInt(compStyle, "padding-right");
};

const getStretchingAssetHeight = (dimensions: string, maxWidth: number, maxHeight = 5000) => {
  let result = 0;
  const [width, height] = dimensions.split("x").map(d => parseInt(d, 10));

  if (width <= maxWidth) {
    result = height;
  } else {
    const scaleBy = maxWidth / width;
    result = height * scaleBy;
  }

  return Math.min(result, maxHeight);
};

export default StretchingAsset;
